"use client";

import type { LanguageCode } from "@sum-video/shared/types/language";
import { useEffect, useRef } from "react";
import {
  clearUserIdentification,
  identifyUser,
} from "@/lib/analytics/userIdentification";
import { isRecentUserRegistration } from "@/lib/email/isRecentUserRegistration";
import { pageLoaded } from "@/lib/pageLoad";
import { captureException } from "@/lib/sentry/captureClient";
import { SentryTag } from "@/lib/sentry/tags";
import { loadSupabaseClient } from "@/lib/supabase/loadClient";
import { api } from "@/lib/trpc/client";

/**
 * Hook to handle user identification in analytics when authentication state changes
 */
export const useAuthAnalytics = (language: LanguageCode): void => {
  const notifyRegistration = api.notify.registration.useMutation();
  const notifyRegistrationRef = useRef(notifyRegistration.mutate);
  const languageRef = useRef(language);
  const notifiedAuthIds = useRef(new Set<string>());
  notifyRegistrationRef.current = notifyRegistration.mutate;
  languageRef.current = language;

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let unmounted = false;
    // supabase-js is ~66 KB gzip: fetching it before load slows the LCP image.
    pageLoaded()
      .then(loadSupabaseClient)
      .then((supabase) => {
        if (unmounted) return;
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
          // A visitor without a session also gets INITIAL_SESSION with no user;
          // only a real sign-out may reset the anonymous PostHog id.
          if (event === "SIGNED_OUT") {
            clearUserIdentification();
            return;
          }

          const user = session?.user;
          if (!user) {
            return;
          }

          identifyUser(user, { captureLogin: event === "SIGNED_IN" });
          const shouldNotify =
            (event === "SIGNED_IN" ||
              event === "INITIAL_SESSION" ||
              event === "USER_UPDATED") &&
            isRecentUserRegistration(user) &&
            !notifiedAuthIds.current.has(user.id);
          if (shouldNotify) {
            notifiedAuthIds.current.add(user.id);
            notifyRegistrationRef.current({ language: languageRef.current });
          }
        });
        unsubscribe = () => subscription.unsubscribe();
      })
      .catch((error: unknown) => {
        captureException(error, {
          tags: { [SentryTag.Source]: "authAnalytics" },
        });
      });

    return () => {
      unmounted = true;
      unsubscribe?.();
    };
  }, []);
};
